import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSettings, updateSettings } from "../../RTK/settings/Settings";
import {
  Settings,
  Edit2,
  Globe,
  Building2,
  Phone,
  Mail,
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  Image,
  Bot,
  MessageSquare,
  Copyright,
  Layout,
  Lock, 
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { usePermissions } from "../../contexts/PermissionsContext"; 
import EditSettingsModal from "./popups/EditSettingsModal";

// ✅ تعديل Card Component لإضافة حماية زر التعديل
const Card = ({ title, icon: Icon, children, onEdit, canEdit }) => (
  <div className="bg-gray-800 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
    <div className="flex justify-between items-center mb-6">
      <div className="flex items-center gap-3">
        <Icon className="w-6 h-6 text-blue-400" />
        <h2 className="text-xl font-semibold text-gray-100">{title}</h2>
      </div>
      {canEdit ? (
        <button
          onClick={onEdit}
          className="p-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white transition-colors flex items-center gap-2"
        >
          <Edit2 className="w-4 h-4" />
          <span>تعديل</span>
        </button>
      ) : (
        <div className="p-2 rounded-lg bg-slate-700/50 border border-slate-600 flex items-center gap-2">
          <Lock className="w-4 h-4 text-slate-400" />
          <span className="text-slate-400 text-sm">محمي</span>
        </div>
      )}
    </div>
    <div className="space-y-4">{children}</div>
  </div>
);

const DataDisplay = ({ label, value, icon: Icon }) => (
  <div className="flex items-center gap-3 bg-gray-700 rounded-lg p-3">
    {Icon && <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />}
    <div className="flex-1 min-w-0">
      <p className="text-sm text-gray-400">{label}</p>
      <p className="text-gray-100 truncate">{value || "—"}</p>
    </div>
  </div>
);

const SettingsPage = () => {
  const dispatch = useDispatch();
  const { hasPermission } = usePermissions(); 
  
  // ✅ فحص الصلاحية
  const canUpdateSettings = hasPermission("update_website_settings");

  const { settings, status } = useSelector((state) => state.settings);
  const [modalData, setModalData] = useState({
    isOpen: false,
    section: "",
    data: null,
  });

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  // ✅ حماية handleEditClick
  const handleEditClick = (section, data) => {
    if (!canUpdateSettings) {
      toast.error("ليس لديك صلاحية لتعديل إعدادات الموقع 🔒", {
        position: "top-right",
        rtl: true,
        theme: "dark",
      });
      return;
    }

    if (!data) {
      toast.error("البيانات غير متوفرة", {
        position: "top-right",
        rtl: true,
        theme: "dark",
      });
      return;
    }

    const sectionData = getSectionData(data, section);
    if (!sectionData || Object.keys(sectionData).length === 0) {
      toast.error("لا توجد بيانات للتعديل", {
        position: "top-right",
        rtl: true,
        theme: "dark",
      });
      return;
    }

    setModalData({
      isOpen: true,
      section,
      data: sectionData,
    });
  };

  const handleModalClose = () => {
    setModalData({
      isOpen: false,
      section: "",
      data: null,
    });
  };

  const handleModalSave = async (updatedData) => {
    if (!canUpdateSettings) {
      toast.error("ليس لديك صلاحية لحفظ التغييرات 🔒", {
        position: "top-right",
        rtl: true,
        theme: "dark",
      });
      return;
    }

    try {
      const loadingToast = toast.loading("جاري حفظ التغييرات...", {
        position: "top-right",
        rtl: true,
        theme: "dark",
      });
  
      await dispatch(updateSettings(updatedData)).unwrap();
  
      toast.update(loadingToast, {
        render: "تم حفظ التغييرات بنجاح",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      });
  
      handleModalClose();
      dispatch(fetchSettings());
    } catch (error) {
      toast.error(error?.message || "حدث خطأ أثناء الحفظ", {
        position: "top-right",
        rtl: true,
        theme: "dark",
      });
    }
  };

  const getSectionData = (data, section) => {
    if (!data) return {};

    switch (section) {
      case "basic":
        return {
          name: data.name || "",
          description: data.description || "",
          main_language: data.main_language || "",
        };
      case "media":
        return {
          logo: data.logo || "",
          favicon: data.favicon || "",
        };
      case "contact":
        return {
          address: data.address || "",
          phone: data.phone || "",
          email: data.email || "",
        };
      case "social":
        return {
          facebook_url: data.facebook_url || "",
          twitter_url: data.twitter_url || "",
          instagram_url: data.instagram_url || "",
          youtube_url: data.youtube_url || "",
        };
      case "footer":
        return {
          copyright_text: data.copyright_text || "",
          footer_text: data.footer_text || "",
        };
      case "integration":
        return {
          twilio_account_sid: data.twilio_account_sid || "",
          twilio_auth_token: data.twilio_auth_token || "",
          twilio_phone_number: data.twilio_phone_number || "",
          email_sender_email: data.email_sender_email || "",
          email_sender_name: data.email_sender_name || "",
          telegram_bot_key: data.telegram_bot_key || "",
          telegram_chat_id: data.telegram_chat_id || "",
        };
      default:
        return {};
    }
  };

  const getSectionTitle = (section) => {
    switch (section) {
      case "basic":
        return "المعلومات الأساسية";
      case "media":
        return "الوسائط";
      case "contact":
        return "معلومات الاتصال";
      case "social":
        return "وسائل التواصل الاجتماعي";
      case "footer":
        return "معلومات التذييل";
      case "integration":
        return "إعدادات التكامل";
      default:
        return "";
    }
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-blue-400 text-xl">جاري التحميل...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center space-x-4 mb-8">
          <Settings className="w-8 h-8 text-blue-400" />
          <h1 className="text-3xl font-bold text-gray-100">إعدادات الموقع</h1>
        </div>

        {/* ✅ رسالة تحذيرية لو مافيش صلاحية */}
        {!canUpdateSettings && (
          <div className="mb-6 flex items-center gap-3 p-4 bg-orange-900/20 border border-orange-500/30 rounded-lg">
            <Lock className="w-5 h-5 text-orange-400 flex-shrink-0" />
            <div>
              <p className="text-orange-300 font-semibold">
                عرض فقط - ليس لديك صلاحية التعديل
              </p>
              <p className="text-orange-400 text-sm mt-1">
                يمكنك مشاهدة الإعدادات ولكن لا يمكنك تعديلها
              </p>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Basic Information */}
          <Card
            title="معلومات أساسية"
            icon={Globe}
            onEdit={() => handleEditClick("basic", settings)}
            canEdit={canUpdateSettings} // ✅ تمرير الصلاحية
          >
            <DataDisplay label="اسم الموقع" value={settings?.name} />
            <DataDisplay
              label="الوصف"
              value={settings?.description?.replace(/<[^>]*>/g, "")}
            />
            <DataDisplay
              label="اللغة الرئيسية"
              value={settings?.main_language}
            />
          </Card>

          {/* Media */}
          <Card
            title="الوسائط"
            icon={Image}
            onEdit={() => handleEditClick("media", settings)}
            canEdit={canUpdateSettings} // ✅ تمرير الصلاحية
          >
            <DataDisplay
              label="شعار الموقع"
              value={settings?.logo}
              icon={Image}
            />
            <DataDisplay
              label="الأيقونة المفضلة"
              value={settings?.favicon}
              icon={Image}
            />
            {settings?.logo && (
              <div className="mt-2">
                <img
                  src={settings.logo}
                  alt="Logo Preview"
                  className="h-20 object-contain mx-auto"
                />
              </div>
            )}
          </Card>

          {/* Contact Information */}
          <Card
            title="معلومات الاتصال"
            icon={Phone}
            onEdit={() => handleEditClick("contact", settings)}
            canEdit={canUpdateSettings} // ✅ تمرير الصلاحية
          >
            <DataDisplay
              label="العنوان"
              value={settings?.address}
              icon={Building2}
            />
            <DataDisplay
              label="رقم الهاتف"
              value={settings?.phone}
              icon={Phone}
            />
            <DataDisplay
              label="البريد الإلكتروني"
              value={settings?.email}
              icon={Mail}
            />
          </Card>

          {/* Social Media */}
          <Card
            title="التواصل الاجتماعي"
            icon={Globe}
            onEdit={() => handleEditClick("social", settings)}
            canEdit={canUpdateSettings} // ✅ تمرير الصلاحية
          >
            <DataDisplay
              label="فيسبوك"
              value={settings?.facebook_url}
              icon={Facebook}
            />
            <DataDisplay
              label="تويتر"
              value={settings?.twitter_url}
              icon={Twitter}
            />
            <DataDisplay
              label="انستغرام"
              value={settings?.instagram_url}
              icon={Instagram}
            />
            <DataDisplay
              label="يوتيوب"
              value={settings?.youtube_url}
              icon={Youtube}
            />
          </Card>

          {/* Footer Information */}
          <Card
            title="معلومات التذييل"
            icon={Copyright}
            onEdit={() => handleEditClick("footer", settings)}
            canEdit={canUpdateSettings} // ✅ تمرير الصلاحية
          >
            <DataDisplay
              label="نص حقوق النشر"
              value={settings?.copyright_text}
              icon={Copyright}
            />
            <DataDisplay
              label="نص التذييل"
              value={settings?.footer_text}
              icon={Layout}
            />
          </Card>

          {/* Integration Settings */}
          <Card
            title="إعدادات التكامل"
            icon={Bot}
            onEdit={() => handleEditClick("integration", settings)}
            canEdit={canUpdateSettings} // ✅ تمرير الصلاحية
          >
            <DataDisplay
              label="Twilio Account SID"
              value={settings?.twilio_account_sid}
            />
            <DataDisplay label="Twilio Auth Token" value="*****" />
            <DataDisplay
              label="Twilio Phone Number"
              value={settings?.twilio_phone_number}
              icon={Phone}
            />
            <DataDisplay
              label="Email Sender"
              value={settings?.email_sender_email}
              icon={Mail}
            />
            <DataDisplay
              label="Email Sender Name"
              value={settings?.email_sender_name}
            />
            <DataDisplay label="Telegram Bot Key" value="*****" icon={Bot} />
            <DataDisplay
              label="Telegram Chat ID"
              value={settings?.telegram_chat_id}
              icon={MessageSquare}
            />
          </Card>
        </div>
      </div>

      {/* ✅ Modal يظهر فقط لو عنده صلاحية */}
      {modalData.isOpen && canUpdateSettings && (
        <EditSettingsModal
          isOpen={modalData.isOpen}
          onClose={handleModalClose}
          onSave={handleModalSave}
          initialData={modalData.data}
          sectionTitle={getSectionTitle(modalData.section)}
        />
      )}

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={true}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
    </div>
  );
};

export default SettingsPage;